import { api } from '../../lib/api.ts';
import type { Body } from '../../lib/body.ts';

export type NinmunStatus = 'SIGNED' | 'SIGNING' | 'GENERATED' | 'NONE';

export interface StatsMeetingRow {
  id: string;
  title: string;
  year: number;
  sequence: number;
  scheduledAt: string;
  status: 'SCHEDULED' | 'AGENDA_FINALIZED' | 'CANCELLED';
  agendaPassedAt: string | null;
  yaumiyya: { status: 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' } | null;
  ninmunStatus: NinmunStatus;
  // Final-copy document ids (null unless the doc is in its final/signed state).
  agendaDocId: string | null;
  yaumiyyaDocId: string | null;
  ninmunDocId: string | null;
  haazireeDocId: string | null;
}

export interface SubmitterStats {
  userId: string;
  name: string;
  nameDv: string | null;
  total: number;
  approved: number;
  rejected: number;
  pending: number;
}

export interface SubmissionStats {
  from: string;
  to: string;
  total: number;
  bySubmitter: SubmitterStats[];
}

export interface LeaderboardRow {
  userId: string;
  name: string;
  nameDv: string | null;
  role: string;
  approved: number;
  total: number;
}

export interface Leaderboard {
  year: number;
  rows: LeaderboardRow[];
}

export type MeetingCadence = 'WEEKLY' | 'MONTHLY';

export interface CompliancePeriod {
  start: string;
  end: string;
  met: boolean;
}

export interface MeetingCompliance {
  cadence: MeetingCadence | null;
  year?: number;
  totalPeriods?: number;
  metPeriods?: number;
  missedPeriods?: number;
  percentage?: number;
  periods?: CompliancePeriod[];
  missed?: Array<{ start: string; end: string }>;
  currentPeriod?: CompliancePeriod | null;
}

// One meeting in a member's attendance series — a dot on the attendance page.
export interface AttendanceMeeting {
  year: number;
  sequence: number;
  scheduledAt: string;
  attended: boolean;
}

export interface AttendanceRow {
  name: string;
  role: string;
  attended: number;
  absent: number;
  total: number;
  rate: number;
  // Per-meeting attended/absent, oldest→newest, for the expandable dots strip.
  meetings: AttendanceMeeting[];
}

export interface AttendanceStats {
  meetingsCount: number;
  rows: AttendanceRow[];
}

export const statsApi = {
  meetings: (body?: Body, termId?: string) =>
    api.get<{ meetings: StatsMeetingRow[] }>('/stats/meetings', {
      params: { ...(body ? { body } : {}), ...(termId ? { termId } : {}) },
    }).then((r) => r.data.meetings),
  submissions: (fromISO: string, toISO: string, body?: Body) =>
    api.get<SubmissionStats>('/stats/submissions', { params: { from: fromISO, to: toISO, ...(body ? { body } : {}) } }).then((r) => r.data),
  // Per-body cadence/quorum compliance; the body picks which BodySettings row
  // drives the requirement. Defaults to COUNCIL on the server when omitted.
  compliance: (body?: Body, year?: number) =>
    api.get<MeetingCompliance>('/stats/compliance', {
      params: { ...(body ? { body } : {}), ...(year ? { year } : {}) },
    }).then((r) => r.data),
  // Home-page leaderboard: this year's elected councilors ranked by approved items.
  leaderboard: (body?: Body) =>
    api.get<Leaderboard>('/stats/leaderboard', { params: body ? { body } : undefined }).then((r) => r.data),
  // Per-councilor attendance across a body's approved-yaumiyya meetings, optionally
  // scoped to a term. Members only (SG/bureau/observers excluded server-side).
  attendance: (body?: Body, termId?: string) =>
    api.get<AttendanceStats>('/stats/attendance', {
      params: { ...(body ? { body } : {}), ...(termId ? { termId } : {}) },
    }).then((r) => r.data),
};
